import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import cardService from '../services/cardService';
import inventoryService from '../services/inventoryService';
import wishlistService from '../services/wishlistService';
import slugService from '../services/slugService';
import productService from '../services/productService';
import insertSetService from '../services/insertSetService';
import CardDetailsPage from '../components/CardDetailsPage';
import Sidebar from '../components/Sidebar';
import SidebarSection from '../components/Sidebar/SidebarSection';
import SidebarRow from '../components/Sidebar/SidebarRow';
import SidebarImage from '../components/Sidebar/SidebarImage';
import CardQuantityAdjuster from '../components/CardQuantityAdjuster';
import WishlistIcon from '../components/WishlistIcon';
import TradeIcon from '../components/TradeIcon';
import usePageTitle from '../utils/usePageTitle';
import { getCardBreadcrumbs } from '../utils/breadcrumbUtils';
import './TradingCardPage.css';
import Legend from '../components/Legend';

const TradingCardPage = () => {
  const { productName, insertSetName, cardSlug } = useParams();
  const { currentUser } = useContext(AuthContext);
  
  const [card, setCard] = useState(null);
  const [product, setProduct] = useState(null);
  const [insertSet, setInsertSet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inventoryItem, setInventoryItem] = useState(null);
  const [wishlistItem, setWishlistItem] = useState(null);
  const [addSuccess, setAddSuccess] = useState(null);

  // Set the page title using hierarchy of Card - Insert Set - Product
  usePageTitle(
    card && insertSet && product 
      ? [
          card.name, 
          insertSet.name, 
          `${product.name} (${product.year})`
        ] 
      : 'Card Details'
  );

  // Fetch card details
  useEffect(() => {
    const fetchCardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        let fetchedCard;
        
        // Extract ID from the cardSlug parameter
        if (cardSlug) {
          const parsedSlug = slugService.parseSlugWithId(cardSlug);
          if (!parsedSlug.id) {
            throw new Error('Could not extract ID from card slug');
          }
          
          // Fetch the card by ID
          fetchedCard = await cardService.getCardById(parsedSlug.id);
        } else {
          throw new Error('No card identifier provided');
        }
        
        if (!fetchedCard || !fetchedCard.id) {
          throw new Error('Invalid card data returned from API');
        }
        
        setCard(fetchedCard);
        
        // If user is logged in, check if card is in inventory/wishlist
        if (currentUser) {
          try {
            const inventory = await inventoryService.getUserInventoryByCard(fetchedCard.id);
            setInventoryItem(inventory);
          } catch (err) {
            // Card not in inventory, this is fine
          }
          
          try {
            const wishlist = await wishlistService.getUserWishlistItemByCard(fetchedCard.id);
            setWishlistItem(wishlist);
          } catch (err) {
            // Card not in wishlist, this is fine
          }
        }
      } catch (err) {
        setError('Failed to load card details');
        console.error('Error fetching card details:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCardData();
  }, [productName, insertSetName, cardSlug, currentUser]);
  
  // Fetch product data when needed
  useEffect(() => {
    const fetchProductData = async () => {
      if (!card || !card.productId) return;
      
      // Only fetch if product data isn't available yet
      if (!product) {
        try {
          console.log('Fetching product data for card:', card.name);
          const fetchedProduct = await productService.getProductById(card.productId);
          setProduct(fetchedProduct);
        } catch (err) {
          console.error('Error fetching product data:', err);
          // Don't set error state to avoid disrupting the main page
        }
      }
    };
    
    fetchProductData();
  }, [card, product]);
  
  // Fetch insert set data when needed
  useEffect(() => {
    const fetchInsertSetData = async () => {
      if (!card || !card.insertSetId) return;
      
      // Only fetch if insert set data isn't available yet
      if (!insertSet) {
        try {
          console.log('Fetching insert set data for card:', card.name);
          const fetchedInsertSet = await insertSetService.getInsertSetById(card.insertSetId);
          setInsertSet(fetchedInsertSet);
        } catch (err) {
          console.error('Error fetching insert set data:', err);
          // Don't set error state to avoid disrupting the main page
        }
      }
    };
    
    fetchInsertSetData();
  }, [card, insertSet]);

  // Render the sidebar with product and insert set information
  const renderSidebar = () => {
    if (!card) return null;
    
    return (
      <Sidebar title="Card Information">
        {product && product.imageUrl && (
          <SidebarImage 
            src={product.imageUrl}
            alt={product.name}
          />
        )}
        {product && (
          <SidebarSection title="Product Details">
            <SidebarRow
              label="Product"
              value={
                <Link to={slugService.getProductUrl(product)}>
                  {product.name}
                </Link>
              }
            />
            <SidebarRow
              label="Year"
              value={product.year}
            />
            {product.manufacturer && (
              <SidebarRow
                label="Manufacturer"
                value={product.manufacturer}
              />
            )}
          </SidebarSection>
        )}
        
        {insertSet && (
          <SidebarSection title="Insert Set Details">
            <SidebarRow
              label="Set Name"
              value={
                <Link to={slugService.getInsertSetUrl(insertSet, product)}>
                  {insertSet.name}
                </Link>
              }
            />
            {insertSet.description && (
              <SidebarRow
                label="Description"
                value={insertSet.description}
              />
            )}
            {insertSet.odds && (
              <SidebarRow
                label="Pack Odds"
                value={insertSet.odds}
              />
            )}
            {insertSet.totalCards && (
              <SidebarRow
                label="Total Cards"
                value={insertSet.totalCards}
              />
            )}
          </SidebarSection>
        )}
        
        <SidebarSection title="Card Details">
          {card.cardNumber && (
            <SidebarRow
              label="Card Number"
              value={card.cardNumber}
            />
          )}
          {card.serialNumber && (
            <SidebarRow
              label="Serial Number"
              value={card.serialNumber}
            />
          )}
          {card.serialTotal > 0 && (
            <SidebarRow
              label="Serial Total"
              value={card.serialTotal}
            />
          )}
          {card.statedOdds && (
            <SidebarRow
              label="Stated Odds"
              value={card.statedOdds}
            />
          )}
          {inventoryItem && (
            <SidebarRow
              label="In Your Collection"
              value={inventoryItem.quantity}
            />
          )}
          {wishlistItem && (
            <SidebarRow
              label="In Your Wishlist"
              value="Yes"
            />
          )}
        </SidebarSection>
        
        {card.attributes && Object.keys(card.attributes).length > 0 && (
          <SidebarSection title="Attributes">
            {Object.entries(card.attributes).map(([key, value]) => (
              <SidebarRow
                key={key}
                label={key}
                value={value}
              />
            ))}
          </SidebarSection>
        )}
      </Sidebar>
    );
  };

  // Prepare navigation links
  const navigationLinks = {
    back: product ? {
      url: slugService.getProductUrl(product),
      label: `Back`
    } : null,
    view: insertSet && product ? {
      url: slugService.getInsertSetUrl(insertSet, product),
      label: `View all ${insertSet.name} cards`
    } : null
  };

  // Left side actions (quantity adjuster)
  const leftActions = card && (
    <CardQuantityAdjuster 
      card={{ ...card, quantity: inventoryItem ? inventoryItem.quantity : 0 }} 
      className="card-details__icon"
      size={24}
    />
  );

  // Right side actions (trade and wishlist icons)
  const rightActions = card && (
    <>
      <TradeIcon card={card} className="card-details__icon" size={24} />
      <WishlistIcon card={card} className="card-details__icon" size={24} />
    </>
  );

  // Generate breadcrumb items for this card page
  const breadcrumbItems = getCardBreadcrumbs(card, insertSet, product);

  return (
    <CardDetailsPage
      card={card}
      loading={loading}
      error={error}
      cardNumber={card?.cardNumber}
      title={card?.name || ""}
      imageUrl={card?.imageUrl}
      description={card?.description}
      navigationLinks={navigationLinks}
      leftActions={leftActions}
      rightActions={rightActions}
      renderSidebar={renderSidebar}
      alertMessage={addSuccess}
      onCloseAlert={() => setAddSuccess(null)}
      breadcrumbItems={breadcrumbItems}
      footer={<Legend />}
    />
  );
};

export default TradingCardPage;